// Libraries
import { List, ListIcon, ListItem } from '@chakra-ui/react';
// Components
import { ErrorIcon, WarningIcon } from '@atoms/index';
// Types
import { InformationListMessageType, InformationListProps } from './InformationList.types';

/**
 * InformationList component displays a list of error and warning messages.
 *
 * @components
 * @param {InformationListProps} props - The component props.
 * @param {string[]} props.errors - An array of error messages.
 * @param {string[]} props.warnings - An array of warning messages.
 * @returns {ReactElement} The InformationList component.
 */
export const InformationList = ({ errors = [], warnings = [] }: InformationListProps) => {
  /**
   * Retrieves messages with the specified type.
   *
   * @param {string[]} messages - The array of messages.
   * @param {InformationListMessageType} type - The type of the messages.
   * @returns {Array<{ text: string, type: InformationListMessageType }>} The messages with the specified type.
   */
  const getMessagesWithType = (messages: string[], type: InformationListMessageType) =>
    messages.map((message) => ({ text: message, type }));

  /**
   * Retrieves the corresponding icon based on the message type.
   *
   * @param {InformationListMessageType} type - The type of the message.
   * @returns {ReactElement} The icon component for the specified type.
   */
  const getIcon = (type: InformationListMessageType) =>
    type === InformationListMessageType.ERROR ? ErrorIcon : WarningIcon;

  /**
   * Retrieves the corresponding color based on the message type.
   *
   * @param {InformationListMessageType} type - The type of the message.
   * @returns {string} The color value for the specified type.
   */
  const getColor = (type: InformationListMessageType) =>
    type === InformationListMessageType.ERROR ? 'roseVale' : 'ratings.C.dark';

  const messages = [
    ...getMessagesWithType(errors, InformationListMessageType.ERROR),
    ...getMessagesWithType(warnings, InformationListMessageType.WARNING),
  ];

  return (
    <List w='full' border='section' p={2.5} borderRadius='md' maxH={40} overflowY='scroll'>
      {messages.map((message, idx) => (
        <ListItem
          display='grid'
          gridTemplateColumns='20px auto'
          key={`${message.text}-${idx}`}
          color='text.dark'
          fontSize='s'
          _notLast={{ mb: 1.5 }}
        >
          <ListIcon as={getIcon(message.type)} height={4} color={getColor(message.type)} />
          {message.text}
        </ListItem>
      ))}
    </List>
  );
};
